/**
 * EditVatSummaryModal Component
 *
 * Modal shell around `VatSummaryForm`. Owns dialog chrome and forwards
 * save/close to the editor hook. The form itself owns the table layout,
 * inline warning banners, the "Add rate" toolbar action and the nested
 * row-remove confirmation modal.
 *
 * Uses `useTranslation('modules.modals.editVatSummary')` for chrome labels.
 */
import * as React from 'react';
import { Modal } from '../../components/Modal';
import { Button } from '../../components/Button';
import { Icon } from '../../components/icons/Icon';
import { useEnterSubmit } from '../../hooks';
import { useTranslation } from '../../providers/UIProvider';
import { getTestId, type TestIdProps } from '../../utils';
import { type VatRateOption } from '../../components/inputs';
import { VatSummaryForm } from '../forms/vat-summary/VatSummaryForm';
import { useVatRowEditor, type RecomputeVatSummaryFn } from '../forms/vat-summary/hooks';
import type { VatSummaryCurrentData, VatSummaryFormData } from '../forms/vat-summary/utils';
import type { ValidationIssue } from '../../types';

export type { RecomputeVatSummaryFn } from '../forms/vat-summary/hooks';

// Re-export types so existing imports from `EditVatSummaryModal` keep working.
export type {
  VatSummaryItem,
  VatSummaryFormData,
  VatSummaryCurrentData,
  InternalVatRow
} from '../forms/vat-summary/utils';
export { VatSummaryForm } from '../forms/vat-summary/VatSummaryForm';

export interface EditVatSummaryModalProps extends TestIdProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: VatSummaryFormData) => Promise<void>;
  currentData: VatSummaryCurrentData;
  currencyCode?: string | null;
  vatRateOptions?: VatRateOption[];
  /**
   * Validation issues to surface inside the form. `vatSummary.<i>[.<field>]`
   * paths render as per-row/per-cell affordances; `vatSummary` (section-level)
   * and empty-path issues become banners above the table.
   */
  errors?: ValidationIssue[];
  /** Backend recompute of the VAT breakdown from line items
   * (`invoiceComputedVatSummary`). When provided, drives expected-value hints
   * and the "fill from lines" toolbar action. */
  onRecomputeVatSummary?: RecomputeVatSummaryFn;
}

export const EditVatSummaryModal: React.FC<EditVatSummaryModalProps> = ({
  open,
  onClose,
  onSave,
  currentData,
  currencyCode,
  vatRateOptions,
  errors,
  onRecomputeVatSummary,
  testId,
  id
}) => {
  const t = useTranslation('modules.modals.editVatSummary');

  const editor = useVatRowEditor({
    open,
    currentData,
    vatRateOptions,
    onSave,
    onClose,
    errorMessage: t('errors.saveError'),
    recompute: onRecomputeVatSummary
  });

  const closeIfNotSaving = () => {
    if (!editor.saving) onClose();
  };

  const handleEnterKeyDown = useEnterSubmit(editor.handleSave);

  return (
    <Modal
      open={open}
      onClose={closeIfNotSaving}
      size='xs'
      className='w-[90vw]! max-w-screen-2xl'
      testId={getTestId({ testId, id })}
    >
      <Modal.Header
        label={t('title')}
        description={t('description')}
        onClose={closeIfNotSaving}
        variant='white'
        icon={<Icon name='file-invoice-dollar' className='w-6 h-6 text-primary-base' />}
      />

      <Modal.Content className='p-md bg-background-muted' size='full'>
        <div onKeyDown={handleEnterKeyDown}>
          <VatSummaryForm
            editor={editor}
            open={open}
            currentData={currentData}
            currencyCode={currencyCode}
            errors={errors}
          />
        </div>
      </Modal.Content>

      <Modal.Footer variant='white'>
        <Modal.Action>
          <Button variant='ghost' size='md' onClick={closeIfNotSaving} disabled={editor.saving}>
            {t('actions.cancel')}
          </Button>
          <Button variant='primary' size='md' onClick={editor.handleSave} disabled={editor.saving}>
            {editor.saving ? t('actions.saving') : t('actions.save')}
          </Button>
        </Modal.Action>
      </Modal.Footer>
    </Modal>
  );
};

EditVatSummaryModal.displayName = 'EditVatSummaryModal';
